// src/pages/admin/QuoteEditor.js
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import emailjs from '@emailjs/browser';
import {
  doc,
  onSnapshot,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../../lib/firebase";

function money(n) {
  const x = Number(n || 0);
  return x.toLocaleString(undefined, { style: "currency", currency: "USD" });
}

function num(v, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function clamp(n, min, max) {
  return Math.min(Math.max(n, min), max);
}

function round2(n) {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

function computeTotals({ items, discount, shipping, tax }) {
  const safeItems = Array.isArray(items) ? items : [];

  const itemsSubtotal = round2(
    safeItems.reduce((sum, it) => {
      const qty = num(it.qty, 0);
      const unit = num(it.unitPrice, 0);
      return sum + qty * unit;
    }, 0)
  );

  // Discount (applies before shipping + tax)
  let discountAmount = 0;
  if (discount?.type === "percent") {
    discountAmount = round2(itemsSubtotal * (clamp(num(discount.value, 0), 0, 100) / 100));
  } else {
    discountAmount = round2(num(discount?.value, 0));
  }
  discountAmount = clamp(discountAmount, 0, itemsSubtotal);

  const afterDiscount = round2(Math.max(0, itemsSubtotal - discountAmount));

  // Shipping
  let shippingAmount = 0;
  if (shipping?.type === "percent") {
    shippingAmount = round2(afterDiscount * (clamp(num(shipping.value, 0), 0, 100) / 100));
  } else {
    shippingAmount = round2(num(shipping?.value, 0));
  }
  shippingAmount = Math.max(0, shippingAmount);

  const preTaxSubtotal = round2(afterDiscount + shippingAmount);

  // Tax
  let taxAmount = 0;
  if (tax?.type === "percent") {
    taxAmount = round2(preTaxSubtotal * (clamp(num(tax.value, 0), 0, 100) / 100));
  } else {
    taxAmount = round2(num(tax?.value, 0));
  }
  taxAmount = Math.max(0, taxAmount);

  const grandTotal = round2(preTaxSubtotal + taxAmount);

  return {
    itemsSubtotal,
    discountAmount,
    shippingAmount,
    preTaxSubtotal,
    taxAmount,
    grandTotal,
  };
}

function regionLabel(country) {
  const c = (country || "").toLowerCase().trim();
  if (c === "canada" || c.includes("canada")) return "Province";
  if (c === "ireland" || c.includes("ireland")) return "County";
  if (
    c === "united states" ||
    c === "united states of america" ||
    c === "usa" ||
    c === "us" ||
    c.includes("united states")
  )
    return "State";
  return "Region";
}

export default function QuoteEditor() {
    const [sendBusy, setSendBusy] = useState(false);
    const [sendOk, setSendOk] = useState("");
    const [sendErr, setSendErr] = useState("");
  const { id } = useParams(); // quoteId
  const [loading, setLoading] = useState(true);
  const [quote, setQuote] = useState(null);

  const [saving, setSaving] = useState(false);

  // Editable fields (local form state)
  const [title, setTitle] = useState("");
  const [publicMessage, setPublicMessage] = useState("");
  const [internalNotes, setInternalNotes] = useState("");

  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [clientPhone, setClientPhone] = useState("");

  const [addr, setAddr] = useState({
    line1: "",
    line2: "",
    city: "",
    region: "",
    postalCode: "",
    country: "",
  });

  const [items, setItems] = useState([]);
  const [discount, setDiscount] = useState({ type: "amount", value: 0 });
  const [shipping, setShipping] = useState({ type: "amount", value: 0 });
  const [tax, setTax] = useState({ type: "percent", value: 0 });
const [clientOrganization, setClientOrganization] = useState("");

  // Load quote
  useEffect(() => {
    if (!id) return;

    const ref = doc(db, "quotes", id);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const q = snap.exists() ? { id: snap.id, ...snap.data() } : null;
        setQuote(q);
        setLoading(false);

        if (q) {
          setTitle(q.title || "");
          setPublicMessage(q.publicMessage || "");
          setInternalNotes(q.internalNotes || "");

          setClientName(q.clientName || "");
          setClientEmail(q.clientEmail || "");
          setClientPhone(q.clientPhone || "");
          setClientOrganization(q.clientOrganization || "");

          setAddr({
            line1: q.clientAddress?.line1 || "",
            line2: q.clientAddress?.line2 || "",
            city: q.clientAddress?.city || "",
            region: q.clientAddress?.region || "",
            postalCode: q.clientAddress?.postalCode || "",
            country: q.clientAddress?.country || "",
          });

          setItems(Array.isArray(q.items) ? q.items : []);
          setDiscount(q.discount || { type: "amount", value: 0 });
          setShipping(q.shipping || { type: "amount", value: 0 });
          setTax(q.tax || { type: "percent", value: 0 });
        }
      },
      () => {
        setQuote(null);
        setLoading(false);
      }
    );

    return () => unsub();
  }, [id]);

  // Live totals preview
  const liveTotals = useMemo(
    () => computeTotals({ items, discount, shipping, tax }),
    [items, discount, shipping, tax]
  );

  function updateItem(idx, patch) {
    setItems((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], ...patch };
      return next;
    });
  }

  function addItem() {
    setItems((prev) => [
      ...prev,
      { name: "New Item", desc: "", qty: 1, unitPrice: 0 },
    ]);
  }

  function removeItem(idx) {
    setItems((prev) => prev.filter((_, i) => i !== idx));
  }

  async function saveQuote(e) {
    e.preventDefault();
    if (!id) return;

    setSaving(true);
    try {
      const totals = computeTotals({ items, discount, shipping, tax });

      await updateDoc(doc(db, "quotes", id), {
        title: title.trim(),
        publicMessage,
        internalNotes,

        clientName: clientName.trim(),
        clientEmail: clientEmail.trim(),
        clientPhone: clientPhone.trim(),
        clientOrganization: clientOrganization.trim(),

        clientAddress: {
          line1: addr.line1.trim(),
          line2: addr.line2.trim(),
          city: addr.city.trim(),
          region: addr.region.trim(),
          postalCode: addr.postalCode.trim(),
          country: addr.country.trim(),
        },

        items: (Array.isArray(items) ? items : []).map((it) => ({
          name: (it.name || "").trim(),
          desc: (it.desc || "").trim(),
          qty: num(it.qty, 0),
          unitPrice: num(it.unitPrice, 0),
        })),

        discount: {
          type: discount?.type === "percent" ? "percent" : "amount",
          value: num(discount?.value, 0),
        },

        shipping: {
          type: shipping?.type === "percent" ? "percent" : "amount",
          value: num(shipping?.value, 0),
        },

        tax: {
          type: tax?.type === "amount" ? "amount" : "percent",
          value: num(tax?.value, 0),
        },

        totals,
        updatedAt: serverTimestamp(),
      });
    } finally {
      setSaving(false);
    }
  }

  async function sendQuoteEmail() {
  if (!quote?.id) return;

  setSendOk("");
  setSendErr("");

  // Basic validation
  const toEmail = (clientEmail || "").trim();
  if (!toEmail) {
    setSendErr("Client email is empty. Add an email before sending.");
    return;
  }

  setSendBusy(true);
  try {
    // 1) Compute + persist latest totals before sending (so the public link matches)
    const totals = computeTotals({ items, discount, shipping, tax });

    await updateDoc(doc(db, "quotes", quote.id), {
      title: title.trim(),
      publicMessage,
      internalNotes,

      clientName: clientName.trim(),
      clientOrganization: (quote.clientOrganization || "").trim(), // or wire in editor state if you added it
      clientEmail: toEmail,
      clientPhone: clientPhone.trim(),

      clientAddress: {
        line1: addr.line1.trim(),
        line2: addr.line2.trim(),
        city: addr.city.trim(),
        region: addr.region.trim(),
        postalCode: addr.postalCode.trim(),
        country: addr.country.trim(),
      },

      items: (Array.isArray(items) ? items : []).map((it) => ({
        name: (it.name || "").trim(),
        desc: (it.desc || "").trim(),
        qty: num(it.qty, 0),
        unitPrice: num(it.unitPrice, 0),
      })),

      discount: {
        type: discount?.type === "percent" ? "percent" : "amount",
        value: num(discount?.value, 0),
      },

      shipping: {
        type: shipping?.type === "percent" ? "percent" : "amount",
        value: num(shipping?.value, 0),
      },

      tax: {
        type: tax?.type === "amount" ? "amount" : "percent",
        value: num(tax?.value, 0),
      },

      totals,
      updatedAt: serverTimestamp(),
    });

    // 2) Send via EmailJS
    const quoteLink = `${window.location.origin}/quote/${quote.id}`;
    const quoteTotal = money(totals.grandTotal);

    // 🔧 Put your EmailJS IDs here:
    const SERVICE_ID = "service_98c8bqn";
    const TEMPLATE_ID = "template_v4166rv";
    const PUBLIC_KEY = "4piQXAPczRkoIIcrT";

    const templateParams = {
      to_email: toEmail,
      to_name: clientName.trim() || "Client",
      quote_number: quote.quoteNumber || "",
      quote_title: title.trim() || "ESTIMATE",
      quote_total: quoteTotal,
      quote_link: quoteLink,
    };

    await emailjs.send(SERVICE_ID, TEMPLATE_ID, templateParams, PUBLIC_KEY);

    // 3) Mark as sent
    await updateDoc(doc(db, "quotes", quote.id), {
      status: "sent",
      sentAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    setSendOk("Quote email sent.");
  } catch (e) {
    setSendErr(e?.message || String(e));
  } finally {
    setSendBusy(false);
  }
}


  if (loading) return <p className="muted">Loading…</p>;

  if (!quote) {
    return (
      <div>
        <p className="muted">Quote not found.</p>
        <Link className="btn" to="/admin">
          Back
        </Link>
      </div>
    );
  }

  const publicUrl = `${window.location.origin}/quote/${quote.id}`;

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
          alignItems: "flex-start",
        }}
      >
        <div>
          <h2 style={{ marginBottom: 6 }}>
            {quote.quoteNumber || "Quote"}{" "}
            <span className="micro muted" style={{ fontWeight: 400 }}>
              (Editor)
            </span>
          </h2>
          <div className="micro muted">Status: {quote.status || "draft"}</div>
        </div>

        <div style={{ textAlign: "right", minWidth: 260 }}>
          <a className="btn" href={publicUrl} target="_blank" rel="noreferrer">
            View Public
          </a>
          <div className="micro muted" style={{ marginTop: 8 }}>
            <code>{publicUrl}</code>
          </div>
          <div style={{ marginTop: 10, display: "flex", gap: 10, flexWrap: "wrap" }}>
  <button
    type="button"
    className="btn primary"
    onClick={sendQuoteEmail}
    disabled={sendBusy || saving}
    title={!clientEmail?.trim() ? "Add a client email first" : ""}
  >
    {sendBusy ? "Sending…" : "Send Quote"}
  </button>

  <button
    type="button"
    className="btn"
    onClick={() => navigator.clipboard.writeText(publicUrl)}
    disabled={sendBusy}
  >
    Copy Link
  </button>
</div>

{sendOk && <p className="micro muted" style={{ marginTop: 8 }}>{sendOk}</p>}
{sendErr && (
  <p className="micro" style={{ marginTop: 8, color: "crimson" }}>
    {sendErr}
  </p>
)}

        </div>
      </div>

      <form onSubmit={saveQuote} className="form" style={{ marginTop: 16 }}>
        {/* Title */}
        <h3 style={{ marginBottom: 8 }}>Header</h3>
        <label className="field">
          <input
            type="text"
            placeholder="Quote Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            disabled={saving}
          />
        </label>

        {/* Client */}
        <h3 style={{ margin: "18px 0 8px" }}>Client</h3>

        <div style={{ display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
          <label className="field">
            <input
              type="text"
              placeholder="Client Name"
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              disabled={saving}
            />
          </label>

          <label className="field">
            <input
              type="email"
              placeholder="Client Email"
              value={clientEmail}
              onChange={(e) => setClientEmail(e.target.value)}
              disabled={saving}
            />
          </label>

          <label className="field">
            <input
              type="text"
              placeholder="Client Phone"
              value={clientPhone}
              onChange={(e) => setClientPhone(e.target.value)}
              disabled={saving}
            />
          </label>
          <label className="field">
            <input
                type="text"
                placeholder="Organization / Church / Institution"
                value={clientOrganization}
                onChange={(e) => setClientOrganization(e.target.value)}
                disabled={saving}
            />
            </label>
        </div>

        <div style={{ display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
          <label className="field">
            <input
              type="text"
              placeholder="Address Line 1"
              value={addr.line1}
              onChange={(e) => setAddr((p) => ({ ...p, line1: e.target.value }))}
              disabled={saving}
            />
          </label>

          <label className="field">
            <input
              type="text"
              placeholder="Address Line 2"
              value={addr.line2}
              onChange={(e) => setAddr((p) => ({ ...p, line2: e.target.value }))}
              disabled={saving}
            />
          </label>
        </div>

        <div style={{ display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
          <label className="field">
            <input
              type="text"
              placeholder="City"
              value={addr.city}
              onChange={(e) => setAddr((p) => ({ ...p, city: e.target.value }))}
              disabled={saving}
            />
          </label>

          <label className="field">
            <input
              type="text"
              placeholder={regionLabel(addr.country)}
              value={addr.region}
              onChange={(e) => setAddr((p) => ({ ...p, region: e.target.value }))}
              disabled={saving}
            />
          </label>

          <label className="field">
            <input
              type="text"
              placeholder="Postal Code"
              value={addr.postalCode}
              onChange={(e) => setAddr((p) => ({ ...p, postalCode: e.target.value }))}
              disabled={saving}
            />
          </label>

          <label className="field">
            <input
              type="text"
              placeholder="Country"
              value={addr.country}
              onChange={(e) => setAddr((p) => ({ ...p, country: e.target.value }))}
              disabled={saving}
            />
          </label>
        </div>

        {/* Items */}
        <h3 style={{ margin: "18px 0 8px" }}>Items</h3>

        <div style={{ display: "grid", gap: 12 }}>
          {items.length === 0 && <p className="muted">No items yet. Add one below.</p>}

          {items.map((it, idx) => (
            <div
              key={idx}
              style={{
                border: "1px solid rgba(0,0,0,0.1)",
                borderRadius: 14,
                padding: 12,
                background: "rgba(255,255,255,0.7)",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                <strong>Item #{idx + 1}</strong>
                <button type="button" className="btn" onClick={() => removeItem(idx)} disabled={saving}>
                  Remove
                </button>
              </div>

              <div style={{ display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", marginTop: 10 }}>
                <label className="field">
                  <input
                    type="text"
                    placeholder="Item Name"
                    value={it.name || ""}
                    onChange={(e) => updateItem(idx, { name: e.target.value })}
                    disabled={saving}
                  />
                </label>

                <label className="field">
                  <input
                    type="number"
                    step="1"
                    min="0"
                    placeholder="Qty"
                    value={it.qty ?? 0}
                    onChange={(e) => updateItem(idx, { qty: e.target.value })}
                    disabled={saving}
                  />
                </label>

                <label className="field">
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="Unit Price"
                    value={it.unitPrice ?? 0}
                    onChange={(e) => updateItem(idx, { unitPrice: e.target.value })}
                    disabled={saving}
                  />
                </label>

                <div className="micro muted" style={{ alignSelf: "center", textAlign: "right" }}>
                  Line Total:{" "}
                  <strong>
                    {money(num(it.qty, 0) * num(it.unitPrice, 0))}
                  </strong>
                </div>
              </div>

              <label className="field" style={{ marginTop: 10 }}>
                <textarea
                  rows="3"
                  placeholder="Item Description"
                  value={it.desc || ""}
                  onChange={(e) => updateItem(idx, { desc: e.target.value })}
                  disabled={saving}
                />
              </label>
            </div>
          ))}

          <button type="button" className="btn primary" onClick={addItem} disabled={saving}>
            Add Item
          </button>
        </div>

        {/* Pricing */}
        <h3 style={{ margin: "18px 0 8px" }}>Pricing</h3>

        <div style={{ display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
          <div
            style={{
              border: "1px solid rgba(0,0,0,0.1)",
              borderRadius: 14,
              padding: 12,
              background: "rgba(255,255,255,0.7)",
            }}
          >
            <div className="micro muted" style={{ marginBottom: 6 }}>
              Discount
            </div>
            <div style={{ display: "grid", gap: 10, gridTemplateColumns: "120px 1fr" }}>
              <select
                value={discount.type}
                onChange={(e) => setDiscount((p) => ({ ...p, type: e.target.value }))}
                disabled={saving}
                style={{
                  padding: "10px 12px",
                  borderRadius: 12,
                  border: "1px solid rgba(0,0,0,0.15)",
                  background: "rgba(255,255,255,0.8)",
                }}
              >
                <option value="amount">Amount</option>
                <option value="percent">Percent</option>
              </select>

              <input
                type="number"
                step="0.01"
                min="0"
                value={discount.value ?? 0}
                onChange={(e) => setDiscount((p) => ({ ...p, value: e.target.value }))}
                disabled={saving}
                style={{
                  padding: "10px 12px",
                  borderRadius: 12,
                  border: "1px solid rgba(0,0,0,0.15)",
                  background: "rgba(255,255,255,0.8)",
                }}
                placeholder={discount.type === "percent" ? "Percent (0-100)" : "Amount"}
              />
            </div>

            <div className="micro muted" style={{ marginTop: 10 }}>
              Discount Amount: <strong>{money(liveTotals.discountAmount)}</strong>
            </div>
          </div>

          <div
            style={{
              border: "1px solid rgba(0,0,0,0.1)",
              borderRadius: 14,
              padding: 12,
              background: "rgba(255,255,255,0.7)",
            }}
          >
            <div className="micro muted" style={{ marginBottom: 6 }}>
              Shipping
            </div>

            <div style={{ display: "grid", gap: 10, gridTemplateColumns: "120px 1fr" }}>
              <select
                value={shipping.type}
                onChange={(e) => setShipping((p) => ({ ...p, type: e.target.value }))}
                disabled={saving}
                style={{
                  padding: "10px 12px",
                  borderRadius: 12,
                  border: "1px solid rgba(0,0,0,0.15)",
                  background: "rgba(255,255,255,0.8)",
                }}
              >
                <option value="amount">Amount</option>
                <option value="percent">Percent</option>
              </select>

              <input
                type="number"
                step="0.01"
                min="0"
                value={shipping.value ?? 0}
                onChange={(e) => setShipping((p) => ({ ...p, value: e.target.value }))}
                disabled={saving}
                style={{
                  padding: "10px 12px",
                  borderRadius: 12,
                  border: "1px solid rgba(0,0,0,0.15)",
                  background: "rgba(255,255,255,0.8)",
                }}
                placeholder={shipping.type === "percent" ? "Percent (0-100)" : "Amount"}
              />
            </div>

            <div className="micro muted" style={{ marginTop: 10 }}>
              Shipping Amount: <strong>{money(liveTotals.shippingAmount)}</strong>
            </div>
          </div>

          <div
            style={{
              border: "1px solid rgba(0,0,0,0.1)",
              borderRadius: 14,
              padding: 12,
              background: "rgba(255,255,255,0.7)",
            }}
          >
            <div className="micro muted" style={{ marginBottom: 6 }}>
              Tax
            </div>

            <div style={{ display: "grid", gap: 10, gridTemplateColumns: "120px 1fr" }}>
              <select
                value={tax.type}
                onChange={(e) => setTax((p) => ({ ...p, type: e.target.value }))}
                disabled={saving}
                style={{
                  padding: "10px 12px",
                  borderRadius: 12,
                  border: "1px solid rgba(0,0,0,0.15)",
                  background: "rgba(255,255,255,0.8)",
                }}
              >
                <option value="percent">Percent</option>
                <option value="amount">Amount</option>
              </select>

              <input
                type="number"
                step="0.01"
                min="0"
                value={tax.value ?? 0}
                onChange={(e) => setTax((p) => ({ ...p, value: e.target.value }))}
                disabled={saving}
                style={{
                  padding: "10px 12px",
                  borderRadius: 12,
                  border: "1px solid rgba(0,0,0,0.15)",
                  background: "rgba(255,255,255,0.8)",
                }}
                placeholder={tax.type === "percent" ? "Percent (0-100)" : "Amount"}
              />
            </div>

            <div className="micro muted" style={{ marginTop: 10 }}>
              Tax Amount: <strong>{money(liveTotals.taxAmount)}</strong>
            </div>
          </div>
        </div>

        {/* Totals Summary */}
        <div
          style={{
            marginTop: 14,
            border: "1px solid rgba(0,0,0,0.1)",
            borderRadius: 14,
            padding: 14,
            background: "rgba(255,255,255,0.7)",
            maxWidth: 520,
          }}
        >
          <h4 style={{ marginTop: 0, marginBottom: 10 }}>Totals</h4>

          <div style={{ display: "grid", gap: 6 }}>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span className="muted">Items Subtotal</span>
              <span>{money(liveTotals.itemsSubtotal)}</span>
            </div>

            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span className="muted">Discount</span>
              <span>- {money(liveTotals.discountAmount)}</span>
            </div>

            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span className="muted">Shipping</span>
              <span>{money(liveTotals.shippingAmount)}</span>
            </div>

            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span className="muted">Pre-Tax Subtotal</span>
              <span>{money(liveTotals.preTaxSubtotal)}</span>
            </div>

            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span className="muted">Tax</span>
              <span>{money(liveTotals.taxAmount)}</span>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontWeight: 700,
                marginTop: 6,
                fontSize: 18,
              }}
            >
              <span>Total</span>
              <span>{money(liveTotals.grandTotal)}</span>
            </div>
          </div>
        </div>

        {/* Messages */}
        <h3 style={{ margin: "18px 0 8px" }}>Notes</h3>

        <label className="field">
          <textarea
            rows="4"
            placeholder="Public message (visible to client)"
            value={publicMessage}
            onChange={(e) => setPublicMessage(e.target.value)}
            disabled={saving}
          />
        </label>

        <label className="field">
          <textarea
            rows="4"
            placeholder="Internal notes (admin only)"
            value={internalNotes}
            onChange={(e) => setInternalNotes(e.target.value)}
            disabled={saving}
          />
        </label>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button className="btn primary" disabled={saving}>
            {saving ? "Saving…" : "Save Quote"}
          </button>

          {quote.requestId && (
            <Link className="btn" to={`/admin/requests/${quote.requestId}`}>
              Back to Request
            </Link>
          )}

          <Link className="btn" to="/admin">
            Back to Dashboard
          </Link>
        </div>
      </form>
    </div>
  );
}